import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Pinch / wheel / double-tap zoom + pan state for the photo viewer. Keeps a
// { scale, tx, ty } transform, zooms around a focal point, and clamps panning
// so the image can't be dragged fully off screen.

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;

interface ZoomState {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: ZoomState = { scale: 1, tx: 0, ty: 0 };

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

// New transform that keeps the point under (clientX, clientY) fixed while
// scaling to `targetScale`, then clamps the translation to the element bounds.
function computeZoom(
  prev: ZoomState,
  targetScale: number,
  clientX: number,
  clientY: number,
  rect: DOMRect,
): ZoomState {
  const scale = clamp(targetScale, 1, MAX_SCALE);
  if (scale <= 1) return IDENTITY;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const k = scale / prev.scale;
  const tx = (clientX - cx) * (1 - k) + prev.tx * k;
  const ty = (clientY - cy) * (1 - k) + prev.ty * k;
  const maxX = ((scale - 1) * rect.width) / 2;
  const maxY = ((scale - 1) * rect.height) / 2;
  return { scale, tx: clamp(tx, -maxX, maxX), ty: clamp(ty, -maxY, maxY) };
}

export function useImageZoom<T extends HTMLElement>(
  elRef: React.RefObject<T | null>,
) {
  const [state, setState] = useState<ZoomState>(IDENTITY);
  const stateRef = useRef(state);
  // Written in a layout effect, not during render: mutating a ref while
  // rendering isn't safe under concurrent rendering.
  useLayoutEffect(() => {
    stateRef.current = state;
  });
  const panStart = useRef({ tx: 0, ty: 0 });

  const rectOf = useCallback(
    () => elRef.current?.getBoundingClientRect() ?? null,
    [elRef],
  );

  const zoomBy = useCallback(
    (factor: number, x: number, y: number) => {
      const rect = rectOf();
      if (!rect) return;
      setState((prev) => computeZoom(prev, prev.scale * factor, x, y, rect));
    },
    [rectOf],
  );

  const zoomTo = useCallback(
    (scale: number, x: number, y: number) => {
      const rect = rectOf();
      if (!rect) return;
      setState((prev) => computeZoom(prev, scale, x, y, rect));
    },
    [rectOf],
  );

  const toggle = useCallback(
    (x: number, y: number) => {
      const rect = rectOf();
      if (!rect) return;
      setState((prev) =>
        prev.scale > 1.01
          ? IDENTITY
          : computeZoom(prev, DOUBLE_TAP_SCALE, x, y, rect),
      );
    },
    [rectOf],
  );

  const beginPan = useCallback(() => {
    panStart.current = { tx: stateRef.current.tx, ty: stateRef.current.ty };
  }, []);

  const panMove = useCallback(
    (dx: number, dy: number) => {
      const rect = rectOf();
      if (!rect) return;
      setState((prev) => {
        if (prev.scale <= 1) return prev;
        const maxX = ((prev.scale - 1) * rect.width) / 2;
        const maxY = ((prev.scale - 1) * rect.height) / 2;
        return {
          scale: prev.scale,
          tx: clamp(panStart.current.tx + dx, -maxX, maxX),
          ty: clamp(panStart.current.ty + dy, -maxY, maxY),
        };
      });
    },
    [rectOf],
  );

  const reset = useCallback(() => setState(IDENTITY), []);

  return {
    scale: state.scale,
    tx: state.tx,
    ty: state.ty,
    isZoomed: state.scale > 1.01,
    stateRef,
    zoomBy,
    zoomTo,
    toggle,
    beginPan,
    panMove,
    reset,
  };
}
